





















































































































































































































































































































































































































































































































































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { helpForControl } from "./catalogHelpers";
import { buildRcaUrl } from "./filterQueryParams";

import CompletenessBanner from "./CompletenessBanner.vue";
import ScenarioWindowFootnote from "./ScenarioWindowFootnote.vue";
import OverviewTab from "./OverviewTab.vue";
import EpicenterIcon from "./EpicenterIcon.vue";
import MapTab from "./MapTab.vue";
import HotspotsTab from "./HotspotsTab.vue";
import EnterpriseTypesTab from "./EnterpriseTypesTab.vue";
import SectorTab from "./SectorTab.vue";
import GeographyTab from "./GeographyTab.vue";
import DataCoverageTab from "./DataCoverageTab.vue";
import DataAdminTab from "./DataAdminTab.vue";
import DefinitionsTab from "./DefinitionsTab.vue";
import TabExplainer from "./TabExplainer.vue";
import GlobalExplainer from "./GlobalExplainer.vue";
import HelpIcon from "./HelpIcon.vue";
import TruckLoader from "./TruckLoader.vue";

interface TabSpec {
    id: string;
    label: string;
    /** Material Design Icon name (mdi-*) shown before the label. */
    icon?: string;
    /** When true, render the custom concentric-circles "epicenter" SVG instead of an mdi icon. */
    epicenter?: boolean;
    /** When true, this tab is the start of the right-aligned trailing group
     * (Data Coverage / QA / Definitions). margin-left: auto on this tab pushes
     * it and everything after it to the right edge of the bar. */
    trailing?: boolean;
    /** When true, the tab is only shown to users with the sensitive-info permission. */
    adminOnly?: boolean;
}

@Component({
    components: {
        CompletenessBanner,
        ScenarioWindowFootnote,
        OverviewTab,
        EpicenterIcon,
        MapTab,
        HotspotsTab,
        EnterpriseTypesTab,
        SectorTab,
        GeographyTab,
        DataCoverageTab,
        DataAdminTab,
        DefinitionsTab,
        TabExplainer,
        GlobalExplainer,
        HelpIcon,
        TruckLoader,
    },
})
export default class RoadClosureAnalytics extends Vue {
    /** Gates the admin-only Data Admin tab — external users never see it. */
    @Getter("auth/isAllowedSensitiveInfo") isAllowedSensitiveInfo!: boolean;

    // Loading state for the global progress bar.
    loading = false;

    /** True once the first batch of dashboard data has finished loading. false → show
     *  skeleton placeholders, true → show real content. */
    hasLoadedOnce = false;

    // Date range state.
    availableDates: string[] = [];
    loadingDates = false;
    datesError: string | null = null;
    /** Committed date range — what every panel fetches with. start === end means a single
     * day. Only updated on a finished selection (range picker closed, single day picked,
     * or the Full range reset), never mid-selection. */
    startDate: string = "";
    endDate: string = "";
    mapDate: string = "";
    /** Date selector mode and its open state. */
    dateMode: "range" | "single" = "range";
    dateMenu = false;
    /** Inline hint shown under the date field after a bailed range commit (single pick or
     * the same date twice).*/
    rangeHintVisible = false;
    /** Draft values the date pickers bind to — committed to startDate/endDate only when
     * the selection is finished, so panels don't refetch on every pick. */
    rangeDraft: string[] = [];
    singleDraft: string = "";

    // Global filter state — split into a *pending* working copy (what the sidebar widgets
    // bind to) and an *applied* copy (what the dashboard panels actually fetch with). The
    // user mutates pendingFilters freely; nothing reflows downstream until they click
    // "Refresh Data", which copies pendingFilters → filters.
    pendingFilters: {
        sector: string[];
        industry: string[];
        commodity: string[];
        enterprise_type: string[];
    } = {
        sector: [],
        industry: [],
        commodity: [],
        enterprise_type: [],
    };
    filters: {
        sector: string[];
        industry: string[];
        commodity: string[];
        enterprise_type: string[];
    } = {
        sector: [],
        industry: [],
        commodity: [],
        enterprise_type: [],
    };
    /**
     * Cascading option lists derived locally from ``filterCombos``. Recomputed whenever the
     * combos cache or the user's pending selections change, so the dropdowns react instantly
     * to picks without round-tripping per change.
     */
    filterOptions: {
        sector: string[];
        industry: string[];
        commodity: string[];
        enterprise_type: string[];
    } = {
        sector: [],
        industry: [],
        commodity: [],
        enterprise_type: [],
    };

    /**
     * Full set of valid filter combinations for the current date range, fetched once when
     * the dates change. Each row is ``[sector, industry, commodity, et_orig, et_dest]``. The
     * sidebar cascade + the pending-state pruning both read from this set.
     */
    filterCombos: string[][] = [];

    // Active tab index.
    tab: number = 0; // Open on Overview (first tab).

    /** Global scroll-to-top fab visibility (mirrors SCBD: show past 200px). */
    showScroll = false;

    onScroll(e: any): void {
        if (typeof window === "undefined") return;
        const top = window.pageYOffset || e.target.scrollTop || 0;
        this.showScroll = top > 200;
    }

    private allTabs: TabSpec[] = [
        {
            id: "overview",
            label: "Overview",
            icon: "mdi-view-dashboard-outline",
        },
        { id: "hotspots", label: "Hotspots", epicenter: true },
        { id: "geography", label: "Regions", icon: "mdi-map-outline" },
        {
            id: "enterprises",
            label: "Enterprise Types",
            icon: "mdi-domain",
        },
        { id: "sector", label: "Sector", icon: "mdi-sitemap" },
        {
            id: "map",
            label: "Daily Map",
            icon: "mdi-map-search-outline",
            adminOnly: true,
        },
        {
            id: "coverage",
            label: "Data Coverage",
            trailing: true,
            icon: "mdi-database-check",
        },
        {
            id: "admin",
            label: "Data Admin",
            icon: "mdi-shield-lock",
            adminOnly: true,
        },
        {
            id: "definitions",
            label: "Definitions",
            icon: "mdi-book-open-variant",
        },
    ];

    /** The visible tab set — admin-only tabs are dropped for users without the permission. */
    get tabs(): TabSpec[] {
        return this.allTabs.filter(
            (t) => !t.adminOnly || this.isAllowedSensitiveInfo,
        );
    }

    created() {
        eventLoading.$on("loading", this.setLoading);
    }
    beforeDestroy() {
        eventLoading.$off("loading", this.setLoading);
    }
    setLoading(isLoading: boolean) {
        // Flip hasLoadedOnce so the skeleton can fade out and the real content fade in.
        if (!isLoading && this.loading && !this.hasLoadedOnce) {
            this.hasLoadedOnce = true;
        }
        this.loading = isLoading;
    }

    /** Hidden behind the skeleton until the first load completes */
    get mainContentStateClass(): string {
        return this.hasLoadedOnce ? "" : "main-content--hidden";
    }

    mounted() {
        this.fetchDates();
        this.fetchCatalog();
    }

    catalog: any = null;
    definitionsMarkdown: string = "";

    helpFor(controlId: string): string {
        return helpForControl(this.catalog, controlId);
    }

    fetchCatalog() {
        eventLoading.startLoading();
        Vue.axios({ url: endpoints.roadClosureAnalyticsMetricCatalogUrl })
            .then((response: any) => {
                this.catalog = response.data?.catalog ?? null;
                this.definitionsMarkdown =
                    response.data?.definitions_markdown ?? "";
            })
            .catch((err: any) => {
                console.error("[RCA catalog]", err);
            })
            .finally(() => {
                eventLoading.stopLoading();
            });
    }

    fetchDates() {
        this.loadingDates = true;
        this.datesError = null;
        eventLoading.startLoading();
        Vue.axios({ url: endpoints.roadClosureAnalyticsDatesUrl })
            .then((response: any) => {
                const dates: string[] = (response.data || []).map((d: string) =>
                    String(d).slice(0, 10),
                );
                this.availableDates = dates;
                if (dates.length) {
                    this.startDate = dates[0];
                    this.endDate = dates[dates.length - 1];
                    this.mapDate = this.endDate;
                    this.rangeDraft = [this.startDate, this.endDate];
                    this.singleDraft = this.startDate;
                    // Setting startDate/endDate triggers the filter-context watcher, but watchers
                    // sometimes fire before this assignment lands when the dates were already in
                    // place; force a populate.
                    this.fetchFilterCombinations();
                }
            })
            .catch((err: any) => {
                console.error("[RCA dates]", err);
                this.datesError =
                    err?.response?.data?.error ||
                    err?.message ||
                    "Could not load available dates.";
            })
            .finally(() => {
                this.loadingDates = false;
                eventLoading.stopLoading();
            });
    }

    get hasRange(): boolean {
        return Boolean(this.startDate && this.endDate);
    }
    get selectedDayCount(): number {
        if (!this.hasRange) return 0;
        return this.availableDates.filter(
            (d) => d >= this.startDate && d <= this.endDate,
        ).length;
    }

    // -------------------------------------------------------------------------
    // Global filter helpers
    // -------------------------------------------------------------------------

    /** True if any of the four applied filters has at least one selected value. */
    get hasActiveFilters(): boolean {
        return Object.values(this.filters).some(
            (v) => Array.isArray(v) && v.length > 0,
        );
    }

    /** True if pendingFilters has any selected value — drives the Clear button. */
    get hasPendingSelections(): boolean {
        return Object.values(this.pendingFilters).some(
            (v) => Array.isArray(v) && v.length > 0,
        );
    }

    /** True if pendingFilters differs from the applied filters — drives the Refresh button. */
    get hasPendingChanges(): boolean {
        const keys: Array<keyof RoadClosureAnalytics["filters"]> = [
            "sector",
            "industry",
            "commodity",
            "enterprise_type",
        ];
        for (const k of keys) {
            const a = this.pendingFilters[k];
            const b = this.filters[k];
            if (a.length !== b.length) return true;
            const sortedA = [...a].sort();
            const sortedB = [...b].sort();
            for (let i = 0; i < sortedA.length; i++) {
                if (sortedA[i] !== sortedB[i]) return true;
            }
        }
        return false;
    }

    /**
     * Items shown in a filter dropdown. We union the user's pending selections with the
     * backend options so a value the user picked but hasn't applied yet (or one that an
     * upstream applied filter would otherwise hide) never silently disappears.
     */
    filterOptionItems(
        key: keyof RoadClosureAnalytics["filterOptions"],
    ): string[] {
        const opts = this.filterOptions[key] ?? [];
        const current = this.pendingFilters[key] ?? [];
        if (!current.length) return opts;
        const set = new Set(opts);
        const merged = [...opts];
        for (const v of current) {
            if (!set.has(v)) {
                merged.push(v);
                set.add(v);
            }
        }
        return merged;
    }

    clearFilters() {
        this.pendingFilters.sector = [];
        this.pendingFilters.industry = [];
        this.pendingFilters.commodity = [];
        this.pendingFilters.enterprise_type = [];
    }

    /**
     * Copy pending → applied so every panel + the cascading-options endpoint refetch once
     * with the new applied state. Panels watch ``filters`` deeply, so reassigning the four
     * arrays fires their refetch in a single tick.
     */
    applyFilters() {
        // Clicking the greyed-out (inactive) Refresh Data button is a no-op.
        if (!this.hasPendingChanges) return;
        // Controls stay disabled until a fetch batch is still in flight.
        if (this.loading) return;
        this.filters = {
            sector: [...this.pendingFilters.sector],
            industry: [...this.pendingFilters.industry],
            commodity: [...this.pendingFilters.commodity],
            enterprise_type: [...this.pendingFilters.enterprise_type],
        };
    }

    /**
     * Date-range changes invalidate the combos cache; refetch + recompute the cascade.
     * Pending-filter changes only recompute locally — no backend call.
     */
    @Watch("startDate")
    @Watch("endDate")
    onDateRangeChanged() {
        this.fetchFilterCombinations();
    }

    @Watch("pendingFilters", { deep: true })
    onPendingFiltersChanged() {
        if (this.suppressPendingWatcher) return;
        this.recomputeFilterCascade();
        // Auto-apply when the user clears everything (via Clear filters, removing the last
        // chip, or unticking the last value in a dropdown).
        if (!this.hasPendingSelections && this.hasActiveFilters) {
            this.applyFilters();
        }
    }

    /**
     * Guards against the watcher loop that would otherwise fire when ``recomputeFilterCascade``
     * reassigns ``pendingFilters`` to its locally-pruned version. Set true just before the
     * reassignment, cleared on the next tick (after the watcher would have run).
     */
    private suppressPendingWatcher = false;

    private filterCombosFetchScheduled = false;

    /**
     * Pull the full set of (sector, industry, commodity, et_orig, et_dest) combinations for
     * the current date range. We cache them locally and do all cascade + prune work in JS,
     * so the sidebar stays responsive even with many pending selections.
     *
     * A single date-range change calls this up to three times in one tick — the startDate
     * watcher, the endDate watcher, and the explicit call in fetchDates — so combine them via
     * a scheduled flag and hit the combos endpoint once instead of three times.
     */
    fetchFilterCombinations() {
        if (!this.hasRange) return;
        if (this.filterCombosFetchScheduled) return;
        this.filterCombosFetchScheduled = true;
        this.$nextTick(() => {
            this.filterCombosFetchScheduled = false;
            if (this.hasRange) this.loadFilterCombinations();
        });
    }

    private loadFilterCombinations() {
        eventLoading.startLoading();
        Vue.axios({
            url: buildRcaUrl(
                endpoints.roadClosureAnalyticsFilterCombinationsUrl,
                { start_date: this.startDate, end_date: this.endDate },
                null,
            ),
        })
            .then((response: any) => {
                const rows = response.data?.rows;
                this.filterCombos = Array.isArray(rows) ? rows : [];
                this.recomputeFilterCascade();
            })
            .catch((err: any) => {
                console.error("[RCA filter_combinations]", err);
                this.filterCombos = [];
                this.recomputeFilterCascade();
            })
            .finally(() => {
                eventLoading.stopLoading();
            });
    }

    /**
     * Walk the cached combo set top-down, computing each filter's option list using only the
     * filters strictly upstream of it, and pruning ``pendingFilters`` to drop any value that
     * isn't valid under the now-narrowed upstream context. Index layout for each row:
     * ``[0] sector, [1] industry, [2] commodity, [3] et_orig, [4] et_dest``.
     */
    recomputeFilterCascade() {
        const combos = this.filterCombos;
        const allow = (filterValues: string[], v: string) =>
            filterValues.length === 0 || filterValues.includes(v);

        // Pass 1: gather sector options + prune pendingFilters.sector.
        const sectorSet = new Set<string>();
        for (const r of combos) sectorSet.add(r[0]);
        const sectorOpts = [...sectorSet].sort();
        const sectorSelected = sectorOpts.length
            ? this.pendingFilters.sector.filter((v) => sectorSet.has(v))
            : [...this.pendingFilters.sector];

        // Pass 2: industries under pruned-sector context.
        const industrySet = new Set<string>();
        for (const r of combos) {
            if (allow(sectorSelected, r[0])) industrySet.add(r[1]);
        }
        const industryOpts = [...industrySet].sort();
        const industrySelected = industryOpts.length
            ? this.pendingFilters.industry.filter((v) => industrySet.has(v))
            : [...this.pendingFilters.industry];

        // Pass 3: commodities under pruned sector+industry context.
        const commoditySet = new Set<string>();
        for (const r of combos) {
            if (allow(sectorSelected, r[0]) && allow(industrySelected, r[1])) {
                commoditySet.add(r[2]);
            }
        }
        const commodityOpts = [...commoditySet].sort();
        const commoditySelected = commodityOpts.length
            ? this.pendingFilters.commodity.filter((v) => commoditySet.has(v))
            : [...this.pendingFilters.commodity];

        // Pass 4: enterprise types under all three upstream filters. Union et_orig + et_dest
        // because the backend filter is OR-based across the two columns.
        const etSet = new Set<string>();
        for (const r of combos) {
            if (
                allow(sectorSelected, r[0]) &&
                allow(industrySelected, r[1]) &&
                allow(commoditySelected, r[2])
            ) {
                etSet.add(r[3]);
                etSet.add(r[4]);
            }
        }
        const etOpts = [...etSet].sort();
        const etSelected = etOpts.length
            ? this.pendingFilters.enterprise_type.filter((v) => etSet.has(v))
            : [...this.pendingFilters.enterprise_type];

        this.filterOptions = {
            sector: sectorOpts,
            industry: industryOpts,
            commodity: commodityOpts,
            enterprise_type: etOpts,
        };

        const pruned = {
            sector: sectorSelected,
            industry: industrySelected,
            commodity: commoditySelected,
            enterprise_type: etSelected,
        };
        if (this.differsFromPending(pruned)) {
            this.suppressPendingWatcher = true;
            this.pendingFilters = pruned;
            Vue.nextTick(() => {
                this.suppressPendingWatcher = false;
            });
        }
    }

    /** Return true if the locally-pruned filter set differs from the current pendingFilters. */
    private differsFromPending(pruned: Record<string, string[]>): boolean {
        const keys: Array<keyof RoadClosureAnalytics["pendingFilters"]> = [
            "sector",
            "industry",
            "commodity",
            "enterprise_type",
        ];
        for (const k of keys) {
            const a = this.pendingFilters[k] ?? [];
            const b = pruned[k] ?? [];
            if (a.length !== b.length) return true;
            const setA = new Set(a);
            for (const v of b) {
                if (!setA.has(v)) return true;
            }
        }
        return false;
    }
    get minDate(): string {
        return this.availableDates[0] || "";
    }
    get maxDate(): string {
        return this.availableDates[this.availableDates.length - 1] || "";
    }

    get allowedDate(): (val: string) => boolean {
        const dates = this.availableDates;
        return (val: string) => dates.includes(val);
    }

    /**
     * Keep the map date inside the selected range when the range changes.
     */
    @Watch("startDate")
    @Watch("endDate")
    onRangeBoundsChanged() {
        if (!this.mapDate) return;
        if (this.startDate && this.mapDate < this.startDate) {
            this.mapDate = this.startDate;
        } else if (this.endDate && this.mapDate > this.endDate) {
            this.mapDate = this.endDate;
        }
    }

    // -------------------------------------------------------------------------
    // Global date selector — mode switching, batched commit, full-range reset
    // -------------------------------------------------------------------------

    /** Text shown in the date field — the committed range, or a single day. */
    get dateFieldLabel(): string {
        if (!this.startDate || !this.endDate) return "";
        if (this.startDate === this.endDate) return this.startDate;
        return `${this.startDate}  →  ${this.endDate}`;
    }

    /** True when the committed range already spans the full available data extent. */
    get isFullRange(): boolean {
        return (
            this.availableDates.length > 0 &&
            this.startDate === this.minDate &&
            this.endDate === this.maxDate
        );
    }

    /**
     * Switching mode never commits — it only pre-fills the new mode's draft from the
     * current committed dates. The dashboard updates only when the user actually picks.
     */
    @Watch("dateMode")
    onDateModeChanged() {
        this.clearRangeHint();
        if (this.dateMode === "single") {
            this.singleDraft = this.startDate;
        } else {
            this.rangeDraft = [this.startDate, this.endDate];
        }
    }

    /** Show the "pick two different dates" hint. Stays visible until the user makes a valid
     * selection (which calls clearRangeHint) — no auto-dismiss timer. */
    flashRangeHint() {
        this.rangeHintVisible = true;
    }

    /** Hide the range hint — called once the user makes a valid selection. */
    clearRangeHint() {
        this.rangeHintVisible = false;
    }

    /** Instruction shown under the date field so the user always knows what the current mode
     * expects. */
    get dateModeInstruction(): string {
        return this.dateMode === "range"
            ? "Range mode: pick a start date, then an end date (two different days)."
            : "Single-day mode: click one day.";
    }

    /** Auto-close the range picker once both ends are chosen — closing commits the range. */
    @Watch("rangeDraft")
    onRangeDraftChanged(draft: string[]) {
        if (this.dateMode === "range" && draft && draft.length === 2) {
            this.dateMenu = false;
        }
    }

    /** Closing the range picker is the "finished selecting" signal — commit the draft. */
    @Watch("dateMenu")
    onDateMenuChanged(open: boolean) {
        if (!open && this.dateMode === "range") {
            this.commitRange();
        }
    }

    /**
     * Commit the range draft to the dashboard-wide startDate/endDate in one update wave.
     * Range mode's contract is "pick a start and an end" — two DISTINCT dates. A draft with
     * fewer than two dates, or the same date picked twice, is rejected
     * (restoring the draft to the current committed range) and leave the dashboard
     * unchanged.
     */
    commitRange() {
        const picked = (this.rangeDraft || []).filter(Boolean);
        const sorted = [...picked].sort();
        const start = sorted[0];
        const end = sorted[sorted.length - 1];
        if (picked.length < 2 || start === end) {
            this.rangeDraft = [this.startDate, this.endDate];
            this.flashRangeHint();
            return;
        }
        this.clearRangeHint();
        this.rangeDraft = [start, end];
        if (start === this.startDate && end === this.endDate) return;
        this.startDate = start;
        this.endDate = end;
    }

    /** Single-day pick — one click is already a finished selection, so commit + close. */
    onSingleDatePicked(day: string) {
        this.clearRangeHint();
        this.dateMenu = false;
        this.singleDraft = day;
        if (this.startDate === day && this.endDate === day) return;
        this.startDate = day;
        this.endDate = day;
    }

    /** Reset the selector to the full available data extent, in Range mode. */
    resetToFullRange() {
        if (!this.availableDates.length) return;
        this.clearRangeHint();
        const start = this.minDate;
        const end = this.maxDate;
        this.dateMode = "range";
        this.rangeDraft = [start, end];
        this.singleDraft = start;
        if (this.startDate === start && this.endDate === end) return;
        this.startDate = start;
        this.endDate = end;
    }
}
